import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Users, 
  Calendar,
  Download,
  RefreshCw,
  Filter,
  Eye
} from 'lucide-react';
import ApiService from '../services/ApiService';

const TeamDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState('YTD');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [contractorPayments, setContractorPayments] = useState(null);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentsError, setPaymentsError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, [selectedQuarter, selectedYear]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const data = await ApiService.getTeamDashboardData({
        quarter: selectedQuarter,
        year: selectedYear
      });
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      console.log('📊 API failed - showing empty state instead of mock data');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadContractorPayments = async () => {
    setLoadingPayments(true);
    setPaymentsError(null);
    try {
      const data = await ApiService.getContractorPayments();
      setContractorPayments(data);
    } catch (error) {
      console.error('Error loading contractor payments:', error);
      setPaymentsError('Failed to load contractor payment totals.');
      setContractorPayments(null);
    } finally {
      setLoadingPayments(false);
    }
  };

  const getCorrectedMockDashboardData = () => {
    // Only show real team members from your actual database
    return {
      teamMembers: [
        {
          name: "Rich Catron",
          invoiced: 2000.00 + 185.00 + 75.00 + 60.00, // Sum of his work orders
          paid: 1800.00, // Estimated received
          cost: 1500.00 + 138.00 + 60.00 + 35.00, // Sum of his costs
          profit: 587.00, // Total invoiced - total costs = 2320 - 1733 = 587
          margin: 25.30, // (587/2320) * 100 = 25.30%
          currentWeek: 37,
          avgInvoicedPerWeek: 62.70,
          avgProfitPerWeek: 15.86,
          projectedInvoiced: 3234.00,
          projectedPaid: 2910.60,
          projectedProfit: 881.10,
          invoicesNotInXero: 0,
          billsNotInXero: 0,
          netNotInXero: 0
        }
      ],
      quarters: [
        {
          quarter: "YTD",
          totalInvoiced: 2695.00,
          totalPaid: 2362.50,
          totalProfit: 1267.00,
          margin: 47.02
        }
      ],
      summary: {
        totalTeamMembers: 3,
        totalActiveProjects: 6,
        avgMargin: 68.88,
        topPerformer: "Rich Catron"
      }
    };
  };

  const getMockDashboardData = () => {
    return {
      teamMembers: [
        {
          name: "Jason",
          invoiced: 290821.07,
          paid: 175184.60,
          profit: 95636.47,
          margin: 32.83,
          currentWeek: 37,
          avgInvoicedPerWeek: 7869.22,
          avgProfitPerWeek: 2584.50,
          projectedInvoiced: 306659.34,
          projectedPaid: 246203.38,
          projectedProfit: 120533.00,
          invoicesNotInBills: 22349.00,
          billsNotInXero: 14868.60,
          netNotInXero: 7480.40
        },
        {
          name: "Rich",
          invoiced: 435630.56,
          paid: 277110.15,
          profit: 158520.41,
          margin: 36.39,
          currentWeek: 37,
          avgInvoicedPerWeek: 11773.80,
          avgProfitPerWeek: 4284.34,
          projectedInvoiced: 612237.54,
          projectedPaid: 389452.10,
          projectedProfit: 222785.44,
          invoicesNotInBills: 22349.00,
          billsNotInXero: 14868.60,
          netNotInXero: 7480.40
        },
        {
          name: "Brad",
          invoiced: 108174.91,
          paid: 71516.00,
          profit: 36658.91,
          margin: 33.89,
          currentWeek: 37,
          avgInvoicedPerWeek: 2923.65,
          avgProfitPerWeek: 990.78,
          projectedInvoiced: 152029.60,
          projectedPaid: 100508.97,
          projectedProfit: 51520.63,
          invoicesNotInBills: 3669.00,
          billsNotInXero: 1390.00,
          netNotInXero: 2279.00
        },
        {
          name: "Wade",
          invoiced: 36580.00,
          paid: 17435.15,
          profit: 9144.85,
          margin: 34.41,
          currentWeek: 37,
          avgInvoicedPerWeek: 718.38,
          avgProfitPerWeek: 247.16,
          projectedInvoiced: 37335.68,
          projectedPaid: 24503.45,
          projectedProfit: 12852.22,
          invoicesNotInBills: 548.00,
          billsNotInXero: 0,
          netNotInXero: 548.00
        },
        {
          name: "Garrett",
          invoiced: 69152.00,
          paid: 53562.00,
          profit: 15590.00,
          margin: 22.54,
          currentWeek: 37,
          avgInvoicedPerWeek: 1868.97,
          avgProfitPerWeek: 421.35,
          projectedInvoiced: 48593.30,
          projectedPaid: 37638.16,
          projectedProfit: 5000.00,
          invoicesNotInBills: 375.00,
          billsNotInXero: 881.00,
          netNotInXero: -506.00
        }
      ],
      quarters: [
        {
          quarter: "Q1",
          totalInvoiced: 289838.07,
          totalPaid: 213749.40,
          totalProfit: 85504.67,
          margin: 29.50
        },
        {
          quarter: "Q2", 
          totalInvoiced: 280838.07,
          totalPaid: 201748.40,
          totalProfit: 97504.67,
          margin: 28.50
        },
        {
          quarter: "YTD",
          totalInvoiced: 939140.54,
          totalPaid: 692779.07,
          totalProfit: 246361.47,
          margin: 26.23
        }
      ],
      summary: {
        totalTeamMembers: 5,
        totalActiveProjects: 127,
        avgMargin: 31.2,
        topPerformer: "Rich"
      }
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (percent) => {
    return `${percent.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Team Performance Dashboard</h1>
            <p className="text-gray-600 mt-1">Track team performance, financials, and projections</p>
          </div>
          <div className="flex gap-2">
              <select
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="Q1">Q1</option>
                <option value="Q2">Q2</option>
                <option value="Q3">Q3</option>
                <option value="Q4">Q4</option>
                <option value="YTD">YTD</option>
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 2 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <button
                onClick={loadDashboardData}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={() => {/* TODO: Export functionality */}}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
        </div>

        {/* Summary Cards */}
        {dashboardData?.summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Team Members</p>
                  <p className="text-2xl font-bold text-blue-600">{dashboardData.summary.totalTeamMembers}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Projects</p>
                  <p className="text-2xl font-bold text-green-600">{dashboardData.summary.totalActiveProjects}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-green-500" />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Average Margin</p>
                  <p className="text-2xl font-bold text-purple-600">{formatPercentage(dashboardData.summary.avgMargin)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-500" />
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Top Performer</p>
                  <p className="text-2xl font-bold text-orange-600">{dashboardData.summary.topPerformer}</p>
                </div>
                <Users className="w-8 h-8 text-orange-500" />
              </div>
            </div>
          </div>
        )}

          {/* Team Performance Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden mb-8">
        <div className="bg-gray-50 px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Team Performance - {selectedQuarter} {selectedYear}
          </h2>
        </div>
        
        {dashboardData?.teamMembers && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 bg-blue-50">Team Member</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-green-50">Invoiced</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-green-50">Paid</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-green-50">Profit</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-green-50">Margin</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-blue-50">Current Week</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-blue-50">AVG Invoiced Per Week</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-blue-50">Avg Profit Per Week</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-purple-50">Projected Invoiced</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-purple-50">Projected Paid</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-purple-50">Projected Profit</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-orange-50">Invoices Not in Xero</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-orange-50">Bills Not in Xero</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-orange-50">Net Not in Xero</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {dashboardData.teamMembers.map((member, index) => (
                  <tr key={member.name} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                    <td className="px-4 py-3 font-medium text-gray-900 bg-blue-25">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-500" />
                        {member.name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">
                      {formatCurrency(member.invoiced)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatCurrency(member.paid)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-green-600">
                      {formatCurrency(member.profit)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      <span className={`px-2 py-1 rounded text-xs ${
                        member.margin > 35 ? 'bg-green-100 text-green-800' :
                        member.margin > 25 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {formatPercentage(member.margin)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {member.currentWeek}
                    </td>
                    <td className="px-4 py-3 text-right text-blue-700">
                      {formatCurrency(member.avgInvoicedPerWeek)}
                    </td>
                    <td className="px-4 py-3 text-right text-blue-700">
                      {formatCurrency(member.avgProfitPerWeek)}
                    </td>
                    <td className="px-4 py-3 text-right text-purple-700">
                      {formatCurrency(member.projectedInvoiced)}
                    </td>
                    <td className="px-4 py-3 text-right text-purple-700">
                      {formatCurrency(member.projectedPaid)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-purple-600">
                      {formatCurrency(member.projectedProfit)}
                    </td>
                    <td className="px-4 py-3 text-right text-orange-700">
                      {formatCurrency(member.invoicesNotInXero)}
                    </td>
                    <td className="px-4 py-3 text-right text-orange-700">
                      {formatCurrency(member.billsNotInXero)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      <span className={`${member.netNotInXero >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(member.netNotInXero)}
                      </span>
                    </td>
                  </tr>
                ))}
                
                {/* Totals Row */}
                <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                  <td className="px-4 py-3 font-bold text-gray-900">TOTAL</td>
                  <td className="px-4 py-3 text-right font-bold text-green-700">
                    {formatCurrency(dashboardData.teamMembers.reduce((sum, m) => sum + m.invoiced, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-700">
                    {formatCurrency(dashboardData.teamMembers.reduce((sum, m) => sum + m.paid, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-green-600">
                    {formatCurrency(dashboardData.teamMembers.reduce((sum, m) => sum + m.profit, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-700">
                    {formatPercentage(
                      (dashboardData.teamMembers.reduce((sum, m) => sum + m.profit, 0) /
                       dashboardData.teamMembers.reduce((sum, m) => sum + m.invoiced, 0)) * 100
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">-</td>
                  <td className="px-4 py-3 text-right font-bold text-blue-700">
                    {formatCurrency(dashboardData.teamMembers.reduce((sum, m) => sum + m.avgInvoicedPerWeek, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-blue-700">
                    {formatCurrency(dashboardData.teamMembers.reduce((sum, m) => sum + m.avgProfitPerWeek, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-purple-700">
                    {formatCurrency(dashboardData.teamMembers.reduce((sum, m) => sum + m.projectedInvoiced, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-purple-700">
                    {formatCurrency(dashboardData.teamMembers.reduce((sum, m) => sum + m.projectedPaid, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-purple-600">
                    {formatCurrency(dashboardData.teamMembers.reduce((sum, m) => sum + m.projectedProfit, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-orange-700">
                    {formatCurrency(dashboardData.teamMembers.reduce((sum, m) => sum + m.invoicesNotInXero, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-orange-700">
                    {formatCurrency(dashboardData.teamMembers.reduce((sum, m) => sum + m.billsNotInXero, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-700">
                    {formatCurrency(dashboardData.teamMembers.reduce((sum, m) => sum + m.netNotInXero, 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contractor Payments (real Xero payment totals) */}
      <div className="bg-white rounded-lg shadow border overflow-hidden mb-8">
        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Contractor Payments (from Xero)
          </h2>
          <button
            onClick={loadContractorPayments}
            disabled={loadingPayments}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loadingPayments ? 'animate-spin' : ''}`} />
            Pull Payment Totals
          </button>
        </div>

        {contractorPayments && (
          <div className="px-6 pt-3 text-xs text-gray-500">
            {contractorPayments.lastSyncedAt
              ? `Xero payment data last synced ${new Date(contractorPayments.lastSyncedAt).toLocaleString()}`
              : 'No Xero payment sync timestamp found — these totals may be based on unsynced data.'}
          </div>
        )}

        {paymentsError && (
          <div className="px-6 py-4 text-red-600 text-sm">{paymentsError}</div>
        )}

        {!contractorPayments && !loadingPayments && !paymentsError && (
          <div className="px-6 py-8 text-center text-gray-500 text-sm">
            Click "Pull Payment Totals" to load how much has actually been paid to each contractor.
          </div>
        )}

        {contractorPayments && contractorPayments.contractors?.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 bg-blue-50">Contractor</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-green-50">Paid YTD ({new Date().getFullYear()})</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-green-50">Paid Last Year ({new Date().getFullYear() - 1})</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-green-50">Paid Last Year YTD</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {contractorPayments.contractors.map((c) => (
                  <tr key={c.subcontractorName} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{c.subcontractorName}</td>
                    <td className="px-4 py-3 text-right font-medium text-green-700">{formatCurrency(c.paidYtd)}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(c.paidLastYearFull)}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(c.paidLastYearYtd)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                  <td className="px-4 py-3 font-bold text-gray-900">TOTAL</td>
                  <td className="px-4 py-3 text-right font-bold text-green-700">
                    {formatCurrency(contractorPayments.contractors.reduce((sum, c) => sum + c.paidYtd, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-700">
                    {formatCurrency(contractorPayments.contractors.reduce((sum, c) => sum + c.paidLastYearFull, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-700">
                    {formatCurrency(contractorPayments.contractors.reduce((sum, c) => sum + c.paidLastYearYtd, 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {contractorPayments && contractorPayments.contractors?.length === 0 && (
          <div className="px-6 py-8 text-center text-gray-500 text-sm">
            No paid line items found in Xero payment history.
          </div>
        )}
      </div>

      {/* Quarterly Breakdown */}
      {dashboardData?.quarters && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {dashboardData.quarters.map((quarter) => (
            <div key={quarter.quarter} className="bg-white rounded-lg shadow border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{quarter.quarter}</h3>
                <Calendar className="w-6 h-6 text-blue-500" />
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoiced:</span>
                  <span className="font-medium text-green-600">{formatCurrency(quarter.totalInvoiced)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Paid:</span>
                  <span className="font-medium text-gray-700">{formatCurrency(quarter.totalPaid)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Profit:</span>
                  <span className="font-medium text-purple-600">{formatCurrency(quarter.totalProfit)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600">Margin:</span>
                  <span className={`font-bold ${
                    quarter.margin > 30 ? 'text-green-600' : 
                    quarter.margin > 20 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {formatPercentage(quarter.margin)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Performance Insights */}
      <div className="bg-white rounded-lg shadow border p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Performance Insights
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-semibold text-green-800 mb-2">Top Performers</h4>
            <div className="space-y-2">
              {dashboardData?.teamMembers
                .sort((a, b) => b.profit - a.profit)
                .slice(0, 3)
                .map((member, index) => (
                  <div key={member.name} className="flex justify-between items-center">
                    <span className="text-sm text-green-700">
                      {index + 1}. {member.name}
                    </span>
                    <span className="text-sm font-medium text-green-800">
                      {formatCurrency(member.profit)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">Highest Margins</h4>
            <div className="space-y-2">
              {dashboardData?.teamMembers
                .sort((a, b) => b.margin - a.margin)
                .slice(0, 3)
                .map((member, index) => (
                  <div key={member.name} className="flex justify-between items-center">
                    <span className="text-sm text-blue-700">
                      {index + 1}. {member.name}
                    </span>
                    <span className="text-sm font-medium text-blue-800">
                      {formatPercentage(member.margin)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
          
          <div className="bg-orange-50 rounded-lg p-4">
            <h4 className="font-semibold text-orange-800 mb-2">Pending Items</h4>
            <div className="space-y-2">
              {dashboardData?.teamMembers
                .filter(member => member.netNotInXero !== 0)
                .sort((a, b) => Math.abs(b.netNotInXero) - Math.abs(a.netNotInXero))
                .slice(0, 3)
                .map((member, index) => (
                  <div key={member.name} className="flex justify-between items-center">
                    <span className="text-sm text-orange-700">
                      {member.name}
                    </span>
                    <span className={`text-sm font-medium ${
                      member.netNotInXero >= 0 ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {formatCurrency(member.netNotInXero)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default TeamDashboard;
